import React, { useRef, useState } from 'react';
import { Box, capitalize, ClickAwayListener } from '@mui/material';
import { TextInput } from '@fabric-ui/components';
import useClickOutside from '../../hooks/useClickOutside';
import { Filter } from '../../types/filter.types';
import AvDateRangePicker from '../DatePicker/AvDateRangePicker';
import { formatDate, isDateRangeEmpty, isDateRangeNotEmpty } from '../DatePicker/utils';
import { FilterStyledPopper } from '../Select/constants';
import { getDatePickerFromQueryObject, getStringConditionFromDatePicker, selectorWidth } from './utils';

const getEmptyOrNotEmptyDateValue = (filter: Filter) =>
  filter?.expression?.stringCondition
    ? 'empty' in filter.expression.stringCondition
      ? 'empty'
      : 'notEmpty' in filter.expression.stringCondition
        ? 'notEmpty'
        : null
    : null;

const getDisplayValue = (
  dateValue:
    | ReturnType<typeof getEmptyOrNotEmptyDateValue>
    | NonNullable<ReturnType<typeof getDatePickerFromQueryObject>>
    | {
        value: {
          from: Date;
          to: Date;
        };
      }
) => {
  if (typeof dateValue === 'string' && isDateRangeNotEmpty(dateValue)) {
    return 'Is Not Empty';
  }
  if ((typeof dateValue === 'string' && isDateRangeEmpty(dateValue)) || dateValue === null) {
    return 'Is Empty';
  }

  return 'preset' in dateValue && dateValue.preset?.type && dateValue.preset?.period
    ? `${capitalize(dateValue.preset.type)} ${dateValue.preset.count} ${capitalize(dateValue.preset.period)}`
    : [formatDate(dateValue.value.from, 'MMM dd, yyyy'), formatDate(dateValue.value.to, 'MMM dd, yyyy')].join(' - ');
};

interface DateTypeBoxProps {
  filter: Filter;
  setFilter: any;
  size: any;
  isVertical: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}
const DateTypeBox: React.FC<DateTypeBoxProps> = ({ filter, setFilter, size, isVertical, disabled = false, readOnly }) => {
  const [open, setOpen] = useState(false);
  const popperInnerRef = useRef(null);
  const buttonRef = useRef(null);
  const field = (filter.expression?.arrayCondition?.underlying || filter.expression)?.fieldName;

  const handleClose = () => {
    setOpen(false);
  };

  useClickOutside(popperInnerRef, handleClose);

  const dateValue = getEmptyOrNotEmptyDateValue(filter) ||
    getDatePickerFromQueryObject(filter) || {
      value: {
        from: new Date(),
        to: new Date(),
      },
    };

  const displayValue = getDisplayValue(dateValue);

  return (
    <Box key={field}>
      <Box ref={buttonRef} onClick={() => (!disabled ? setOpen(!open) : {})}>
        <TextInput
          size={size}
          style={{ width: isVertical ? selectorWidth : 250 }}
          disableClearButton
          placeholder="date"
          value={displayValue}
          disabled={disabled}
          inputProps={{ readOnly }}
        />
      </Box>
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        sx={{
          minWidth: '400px',
          width: 'fit-content',
          padding: 0,
        }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div>
            <AvDateRangePicker
              value={
                dateValue && typeof dateValue !== 'string'
                  ? { from: new Date(dateValue.value.from), to: new Date(dateValue.value.to) }
                  : dateValue
              }
              activePresetValue={dateValue && typeof dateValue !== 'string' && 'preset' in dateValue ? dateValue.preset : undefined}
              onChange={newDate => {
                const expression = {
                  fieldName: filter.expression?.fieldName,
                  dateCondition: {},
                  ...getStringConditionFromDatePicker(newDate),
                };
                setFilter({
                  ...filter,
                  expression,
                });
              }}
              onClose={handleClose}
              showAsCalendar
              isRange
              showRelativeOptions
              isFilterExpression
            />
          </div>
        </ClickAwayListener>
      </FilterStyledPopper>
    </Box>
  );
};

export default DateTypeBox;
